import type { ExecutionType } from '@unifyapps/defs/types/action';

/**
 * Thrown when a debounce/throttle timer is reset by a subsequent call or explicit cancel.
 * Used by EventHandler to detect that the current action chain should be abandoned.
 */
export class DebounceResetError extends Error {
  constructor(actionId: string) {
    super(`Debounce/throttle reset for action: ${actionId}`);
    this.name = 'DebounceResetError';
  }
}

interface TimerInfo {
  debounceId?: ReturnType<typeof setTimeout>;
  throttleId?: ReturnType<typeof setTimeout>;
  lastExecuteTime?: number;
  throttleResolvers?: (() => void)[];
  pendingReject?: (error: DebounceResetError) => void;
}

const activeTimers = new Map<string, TimerInfo>();

function rejectPending(actionId: string, timers: TimerInfo) {
  if (timers.pendingReject) {
    timers.pendingReject(new DebounceResetError(actionId));
    timers.pendingReject = undefined;
  }
}

/**
 * Applies execution strategy to an action.
 * - undefined/delay: not handled here (simple setTimeout in EventHandler)
 * - debounce: resets timer on each call, executes after silence
 * - throttle: executes immediately, then at most once per window
 */
export function executeWithDebounceThrottle(
  actionId: string,
  executionType: ExecutionType | undefined,
  delayMs: number,
  executor: () => Promise<void>,
): Promise<void> {
  // delay or no type: execute immediately (caller handles delay via setTimeout)
  if (!executionType || executionType === 'delay') {
    return executor();
  }

  if (delayMs === 0) {
    return executor();
  }

  return new Promise<void>((resolve, reject) => {
    let timers = activeTimers.get(actionId);
    if (!timers) {
      timers = {};
      activeTimers.set(actionId, timers);
    }

    const executeAndResolve = async () => {
      try {
        timers.pendingReject = undefined;
        await executor();
        resolve();
      } catch (error) {
        reject(error);
      } finally {
        if (timers.debounceId) {
          clearTimeout(timers.debounceId);
          delete timers.debounceId;
        }
        if (timers.throttleId) {
          clearTimeout(timers.throttleId);
          delete timers.throttleId;
        }
        if (timers.throttleResolvers) {
          timers.throttleResolvers = [];
        }
      }
    };

    if (executionType === 'debounce') {
      if (timers.debounceId) {
        clearTimeout(timers.debounceId);
      }
      rejectPending(actionId, timers);

      timers.pendingReject = reject;
      timers.debounceId = setTimeout(() => {
        executeAndResolve();
      }, delayMs);
    } else if (executionType === 'throttle') {
      const now = Date.now();
      const lastExecute = timers.lastExecuteTime ?? 0;
      const timeSinceLastExecute = now - lastExecute;

      if (timeSinceLastExecute >= delayMs) {
        timers.lastExecuteTime = now;
        executeAndResolve();
      } else if (!timers.throttleId) {
        const remainingDelay = delayMs - timeSinceLastExecute;
        rejectPending(actionId, timers);
        timers.pendingReject = reject;
        timers.throttleId = setTimeout(() => {
          timers.lastExecuteTime = Date.now();
          if (timers.throttleResolvers && timers.throttleResolvers.length > 0) {
            for (const resolver of timers.throttleResolvers) {
              resolver();
            }
            timers.throttleResolvers = [];
          }
          executeAndResolve();
        }, remainingDelay);
        if (!timers.throttleResolvers) {
          timers.throttleResolvers = [];
        }
        timers.throttleResolvers.push(resolve);
        return;
      } else {
        if (!timers.throttleResolvers) {
          timers.throttleResolvers = [];
        }
        timers.throttleResolvers.push(resolve);
        return;
      }
    }
  });
}

export function cancelDebounceThrottle(actionId: string): void {
  const timers = activeTimers.get(actionId);
  if (timers) {
    if (timers.debounceId) clearTimeout(timers.debounceId);
    if (timers.throttleId) clearTimeout(timers.throttleId);
    rejectPending(actionId, timers);
    activeTimers.delete(actionId);
  }
}

export function clearDebounceThrottleTimers(actionId: string): void {
  const timers = activeTimers.get(actionId);
  if (timers) {
    if (timers.debounceId) clearTimeout(timers.debounceId);
    if (timers.throttleId) clearTimeout(timers.throttleId);
    activeTimers.delete(actionId);
  }
}

export function clearAllDebounceThrottleTimers(): void {
  activeTimers.forEach((_, actionId) => {
    clearDebounceThrottleTimers(actionId);
  });
}
